/**
 * GetPremiumMarkdownButton
 *
 * Upgrade CTA rendered from assistant markdown and generated previews.
 *
 * Primary responsibilities:
 * - Open the shared plan picker from markdown surfaces
 * - Keep premium CTA styling consistent across chat renderers
 *
 * Dependencies:
 * - ChatbotUIContext
 */

'use client'

import { useCallback, useContext, useMemo, type ComponentProps } from 'react'
import { Sparkles } from 'lucide-react'
import { ChatbotUIContext } from '@/context/context'
import { cn } from '@/lib/utils'

type PremiumButtonPayload = {
  label?: string
  reason?: string
}

export interface GetPremiumMarkdownButtonProps
  extends Omit<ComponentProps<'button'>, 'children' | 'onClick' | 'type'> {
  label?: string
  payloadText?: string
  reason?: string
}

function readPayload(payloadText?: string): PremiumButtonPayload {
  const trimmed = payloadText?.trim()
  if (!trimmed) return {}

  try {
    const parsed = JSON.parse(trimmed) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {}
    }

    const record = parsed as Record<string, unknown>

    return {
      label: typeof record.label === 'string' ? record.label : undefined,
      reason: typeof record.reason === 'string' ? record.reason : undefined
    }
  } catch {
    return {
      label: trimmed.length <= 32 ? trimmed : undefined
    }
  }
}

// --------------

export function GetPremiumMarkdownButton({
  className,
  label,
  payloadText,
  reason,
  ...props
}: GetPremiumMarkdownButtonProps) {
  const { setIsPaywallOpen } = useContext(ChatbotUIContext)
  const payload = useMemo(() => readPayload(payloadText), [payloadText])
  const buttonLabel = label ?? payload.label ?? 'Get Premium'
  const openReason = reason ?? payload.reason ?? 'upgrade_cta'

  const openPremium = useCallback(() => {
    setIsPaywallOpen(true)
    window.dispatchEvent(
      new CustomEvent('losi:open-paywall', {
        detail: {
          reason: openReason,
          source: 'markdown'
        }
      })
    )
  }, [openReason, setIsPaywallOpen])

  return (
    <button
      className={cn(
        'focus:ring-primary/40 group relative inline-flex h-10 items-center justify-center overflow-hidden rounded-full border border-white/15 bg-zinc-950 px-5 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-px hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-60 dark:bg-white dark:text-zinc-950',
        className
      )}
      onClick={openPremium}
      type="button"
      {...props}
    >
      <span className="relative z-10 inline-flex items-center gap-2">
        <Sparkles className="size-4" />
        {buttonLabel}
      </span>
      <span
        aria-hidden="true"
        className="animate-rainbow absolute inset-x-4 bottom-0 h-1.5 rounded-full bg-[linear-gradient(90deg,hsl(var(--color-1)),hsl(var(--color-5)),hsl(var(--color-3)),hsl(var(--color-4)),hsl(var(--color-2)))] bg-[length:200%] blur-[6px] transition-opacity duration-300 group-hover:opacity-100"
      />
      <span
        aria-hidden="true"
        className="absolute inset-0 rounded-full bg-[linear-gradient(110deg,transparent_0%,rgba(255,255,255,.18)_45%,transparent_60%)] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
      />
    </button>
  )
}
